import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { StatusBadge } from "../components/StatusBadge";
import { Plus, Search, Trash2, MoreHorizontal, Eye, Pencil, Archive, ArchiveRestore, AlertTriangle, Flame } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatIDRShort, clientName, formatApiError } from "../lib/format";
import { BOOKING_STATUSES, BOOKING_STATUS_LABEL, DIVISIONS, hasFinancialAccess, isManagement, canDelete, canForceDelete } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import ForceDeleteModal from "../components/ForceDeleteModal";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";

const empty = {
  client_id: "", event_name: "", event_type: "Wedding", event_date: "",
  start_time: "10:00", venue: "", location: "", guest_count: 0,
  package_name: "", total_value: 0, discount: 0, assigned_admin: "",
  status: "Inquiry", notes: "", division_ids: [],
  is_out_of_town: false, include_transport: false, out_of_town_surcharge: 10000000,
};

export default function Bookings() {
  const { user } = useAuth();
  const financial = hasFinancialAccess(user);
  const mgmt = isManagement(user);
  const owner = canDelete(user);
  const canForce = canForceDelete(user);
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [fstat, setFstat] = useState("all");
  const [fMonth, setFMonth] = useState("all");
  const [fYear, setFYear] = useState("all");
  const [groupByMonth, setGroupByMonth] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConflict, setDeleteConflict] = useState(null);
  const [forceDeleteTarget, setForceDeleteTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveReason, setArchiveReason] = useState("");
  const [tab, setTab] = useState("active");

  const load = () => api.get("/bookings", { params: { archived: tab === "archived" ? "true" : tab === "all" ? "all" : undefined } }).then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    if (mgmt) api.get("/clients").then(({ data }) => setClients(data));
  }, [mgmt, tab]);

  const filtered = useMemo(() => rows.filter((b) => {
    if (fstat !== "all" && b.status !== fstat) return false;
    if (fMonth !== "all" || fYear !== "all") {
      if (!b.event_date) return false;
      const d = new Date(b.event_date);
      if (fMonth !== "all" && d.getMonth() !== Number(fMonth)) return false;
      if (fYear !== "all" && d.getFullYear() !== Number(fYear)) return false;
    }
    if (q) {
      const s = q.toLowerCase();
      const cn = clientName(b.client).toLowerCase();
      if (!cn.includes(s) && !(b.booking_code || "").toLowerCase().includes(s) &&
          !(b.event_name || "").toLowerCase().includes(s) &&
          !(b.venue || "").toLowerCase().includes(s)) return false;
    }
    return true;
  }).sort((a, b) => (b.event_date || "").localeCompare(a.event_date || "")), [rows, fstat, q, fMonth, fYear]);

  const availableYears = useMemo(() => {
    const ys = new Set();
    rows.forEach((r) => { if (r.event_date) ys.add(new Date(r.event_date).getFullYear()); });
    return Array.from(ys).sort((a, b) => b - a);
  }, [rows]);
  const grouped = useMemo(() => {
    const groups = {};
    const monthNames = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
    if (!groupByMonth) {
      return [["all", { label: null, items: filtered }]];
    }
    filtered.forEach((b) => {
      if (!b.event_date) { (groups["_none"] = groups["_none"] || { label: "Tanpa Tanggal", items: [] }).items.push(b); return; }
      const d = new Date(b.event_date);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
      if (!groups[key]) groups[key] = { label, items: [] };
      groups[key].items.push(b);
    });
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  }, [filtered, groupByMonth]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/bookings", { ...form, total_value: Number(form.total_value), discount: Number(form.discount), guest_count: Number(form.guest_count), out_of_town_surcharge: Number(form.out_of_town_surcharge) });
      toast.success("Pemesanan berhasil dibuat");
      setOpen(false); setForm(empty); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const toggleDiv = (id) => {
    setForm((f) => ({ ...f, division_ids: f.division_ids.includes(id) ? f.division_ids.filter((x) => x !== id) : [...f.division_ids, id] }));
  };

  const doDelete = async ({ reason }) => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/bookings/${deleteTarget.id}`, { headers: reason ? { "X-Delete-Reason": reason } : {} });
      toast.success("Pemesanan dihapus (Recycle Bin 30 hari)");
      setDeleteTarget(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.relations) {
        setDeleteTarget(null);
        setDeleteConflict({ booking: deleteTarget, relations: d.relations });
      } else {
        toast.error(formatApiError(d));
      }
    }
  };

  const openDelete = async (b) => {
    try {
      const { data: rel } = await api.get(`/bookings/${b.id}/relations`);
      if (rel.total > 0) { setDeleteConflict({ booking: b, relations: rel }); return; }
      setDeleteTarget(b);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doArchive = async () => {
    if (!archiveTarget) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${archiveTarget.id}/archive`, { reason: archiveReason.trim() });
      toast.success("Pemesanan diarsipkan");
      setArchiveTarget(null); setArchiveReason(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doRestore = async (b) => {
    try {
      await api.post(`/bookings/${b.id}/restore`);
      toast.success("Pemesanan dipulihkan");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Master Pemesanan</h1>
          <p className="text-sm text-[#5C6066] mt-1">Kode pemesanan: <span className="font-mono">DWG-YYYY-XXXX</span></p>
        </div>
        {mgmt && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand text-white" data-testid="btn-add-booking">
              <Plus className="w-4 h-4 mr-1.5" /> Pemesanan Baru
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="modal-new-booking">
            <DialogHeader><DialogTitle>Buat Pemesanan Baru</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label>Klien *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="booking-client-select"><SelectValue placeholder="Pilih klien" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{clientName(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Nama Acara *</Label>
                <Input className="mt-1.5" value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                  placeholder="Wedding Ayu & Rizky" data-testid="booking-event-name" />
              </div>
              <div><Label>Tanggal Acara *</Label>
                <Input type="date" className="mt-1.5" value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="booking-event-date" /></div>
              <div><Label>Jam Mulai</Label>
                <Input type="time" className="mt-1.5" value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
              <div><Label>Venue</Label>
                <Input className="mt-1.5" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} data-testid="booking-venue" /></div>
              <div><Label>Kota</Label>
                <Input className="mt-1.5" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div><Label>Jumlah Tamu</Label>
                <Input type="number" className="mt-1.5" value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} /></div>
              <div><Label>Nama Paket</Label>
                <Input className="mt-1.5" value={form.package_name} onChange={(e) => setForm({ ...form, package_name: e.target.value })} /></div>
              <div><Label>Total Kontrak (Rp)</Label>
                <Input type="number" className="mt-1.5" value={form.total_value} onChange={(e) => setForm({ ...form, total_value: e.target.value })} data-testid="booking-total-value" /></div>
              <div><Label>Diskon (Rp)</Label>
                <Input type="number" className="mt-1.5" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} /></div>
              <div><Label>Admin PIC</Label>
                <Input className="mt-1.5" value={form.assigned_admin} onChange={(e) => setForm({ ...form, assigned_admin: e.target.value })} /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s}>{BOOKING_STATUS_LABEL[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Divisi Terlibat</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {DIVISIONS.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 p-2.5 rounded border border-[#E5E0D8] bg-white cursor-pointer hover:bg-[#FAF6EE]">
                      <Checkbox checked={form.division_ids.includes(d.id)} onCheckedChange={() => toggleDiv(d.id)}
                        data-testid={`booking-div-${d.id}`} />
                      <div>
                        <div className="text-sm font-medium">{d.short}</div>
                        <div className="text-[11px] text-[#8C9096] leading-tight">{d.name}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <Label>Catatan Umum</Label>
                <Textarea className="mt-1.5" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="col-span-2 p-3 rounded-md border border-amber-200 bg-amber-50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.is_out_of_town} onCheckedChange={(v) => setForm({ ...form, is_out_of_town: !!v })} data-testid="booking-out-of-town" />
                  <div>
                    <div className="text-sm font-semibold text-amber-800">Acara Luar Kota</div>
                    <div className="text-[11px] text-amber-700">Surcharge Rp.10.000.000 otomatis ditambahkan, kecuali paket sudah include transport.</div>
                  </div>
                </label>
                {form.is_out_of_town && (
                  <label className="flex items-center gap-2 cursor-pointer mt-2 pl-6">
                    <Checkbox checked={form.include_transport} onCheckedChange={(v) => setForm({ ...form, include_transport: !!v })} data-testid="booking-include-transport" />
                    <span className="text-sm text-amber-800">Paket sudah include transport (skip surcharge)</span>
                  </label>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={submit} disabled={busy || !form.client_id || !form.event_name || !form.event_date}
                className="bg-brand text-white" data-testid="btn-save-booking">Simpan Pemesanan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Tabs value={tab} onValueChange={setTab} data-testid="bookings-tabs">
          <TabsList>
            <TabsTrigger value="active" data-testid="tab-active">Aktif</TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived">Diarsipkan</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">Semua</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C9096]" />
          <Input placeholder="Cari kode, klien, venue..." value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white" data-testid="booking-search-input" />
        </div>
        <Select value={fstat} onValueChange={setFstat}>
          <SelectTrigger className="w-40 bg-white" data-testid="booking-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s}>{BOOKING_STATUS_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fMonth} onValueChange={setFMonth}>
          <SelectTrigger className="w-32 bg-white" data-testid="booking-month-filter"><SelectValue placeholder="Bulan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Bulan</SelectItem>
            {["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"].map((n, i) => <SelectItem key={i} value={String(i)}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fYear} onValueChange={setFYear}>
          <SelectTrigger className="w-24 bg-white" data-testid="booking-year-filter"><SelectValue placeholder="Tahun" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua</SelectItem>
            {availableYears.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-xs text-[#5C6066] px-2" title="Kelompokkan per Bulan">
          <Checkbox checked={groupByMonth} onCheckedChange={(v) => setGroupByMonth(!!v)} data-testid="booking-group-by-month" />
          Group per Bulan
        </label>
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                <th className="text-left px-5 py-3">Kode</th>
                <th className="text-left px-4 py-3">Klien</th>
                <th className="text-left px-4 py-3">Tanggal</th>
                <th className="text-left px-4 py-3">Venue</th>
                <th className="text-left px-4 py-3">Divisi</th>
                {financial && <th className="text-right px-4 py-3">Total</th>}
                {financial && <th className="text-right px-4 py-3">Sisa</th>}
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">{financial ? "Bayar" : "Adm."}</th>
                {mgmt && <th className="text-right px-4 py-3 w-20 sticky right-0 bg-[#FAF6EE] shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={mgmt ? 10 : 9} className="text-center py-10 text-[#8C9096]">Belum ada pemesanan.</td></tr>
              )}
              {grouped.map(([key, grp]) => (
                <Fragment key={`grp-${key}`}>
                  {grp.label && (
                    <tr className="bg-[#FAF6EE]">
                      <td colSpan={mgmt ? 10 : 9} className="px-5 py-2 text-xs font-semibold text-[#B7A175] uppercase tracking-wider" data-testid={`booking-group-${key}`}>
                        {grp.label} <span className="text-[#8C9096] font-normal normal-case">· {grp.items.length} acara</span>
                      </td>
                    </tr>
                  )}
                  {grp.items.map((b) => (
                <tr key={b.id} className="group border-t border-[#F0EBE0] hover:bg-[#F7F4EE]" data-testid={`booking-row-${b.booking_code}`}>
                  <td className="px-5 py-3">
                    <Link to={`/bookings/${b.id}`} className="font-mono text-xs text-brand hover:underline">{b.booking_code}</Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{clientName(b.client)}</div>
                    <div className="text-[11px] text-[#8C9096]">{b.event_name}</div>
                  </td>
                  <td className="px-4 py-3">{formatDate(b.event_date)}</td>
                  <td className="px-4 py-3 text-[#5C6066]">{b.venue || "-"}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(b.divisions || []).map((d) => (
                        <Badge key={d.id} variant="outline" className="text-[10px] font-medium">{d.short}</Badge>
                      ))}
                    </div>
                  </td>
                  {financial && <td className="px-4 py-3 text-right font-mono text-xs">{formatIDRShort(b.final_value)}</td>}
                  {financial && <td className="px-4 py-3 text-right font-mono text-xs">{formatIDRShort(b.remaining)}</td>}
                  <td className="px-4 py-3"><StatusBadge status={b.status} label={BOOKING_STATUS_LABEL[b.status]} /></td>
                  <td className="px-4 py-3">
                    {financial ? <StatusBadge status={b.payment_status} /> :
                      <StatusBadge status={b.admin_clear ? "Done" : "Waiting"} label={b.admin_clear ? "Clear" : "Belum"} />}
                  </td>
                  {mgmt && (
                    <td className="px-4 py-3 text-right sticky right-0 bg-white group-hover:bg-[#F7F4EE] shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`booking-actions-${b.booking_code}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem asChild data-testid={`booking-view-${b.booking_code}`}>
                            <Link to={`/bookings/${b.id}`}><Eye className="w-3.5 h-3.5 mr-2"/> Lihat Detail</Link>
                          </DropdownMenuItem>
                          {!b.archived && (
                            <DropdownMenuItem asChild data-testid={`booking-edit-${b.booking_code}`}>
                              <Link to={`/bookings/${b.id}`}><Pencil className="w-3.5 h-3.5 mr-2"/> Edit Pemesanan</Link>
                            </DropdownMenuItem>
                          )}
                          {!b.archived && (
                            <DropdownMenuItem onClick={() => setArchiveTarget(b)} data-testid={`booking-archive-${b.booking_code}`}>
                              <Archive className="w-3.5 h-3.5 mr-2"/> Arsipkan Pemesanan
                            </DropdownMenuItem>
                          )}
                          {b.archived && (
                            <DropdownMenuItem onClick={() => doRestore(b)} data-testid={`booking-restore-${b.booking_code}`}>
                              <ArchiveRestore className="w-3.5 h-3.5 mr-2"/> Pulihkan
                            </DropdownMenuItem>
                          )}
                          {owner && <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openDelete(b)} className="text-rose-700 focus:text-rose-800 focus:bg-rose-50" data-testid={`booking-delete-${b.booking_code}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-2"/> Hapus Permanen
                            </DropdownMenuItem>
                          </>}
                          {canForce && <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setForceDeleteTarget(b)} className="text-rose-900 focus:text-rose-900 focus:bg-rose-100 font-semibold" data-testid={`booking-force-delete-${b.booking_code}`}>
                              <Flame className="w-3.5 h-3.5 mr-2"/> Force Delete Permanen
                            </DropdownMenuItem>
                          </>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  )}
                </tr>
              ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden divide-y divide-[#F0EBE0]">
          {filtered.length === 0 && <div className="p-8 text-center text-[#8C9096] text-sm">Belum ada pemesanan.</div>}
          {filtered.map((b) => (
            <div key={b.id} className="p-4 hover:bg-[#F7F4EE]" data-testid={`booking-card-${b.booking_code}`}>
              <div className="flex justify-between items-start gap-2">
                <Link to={`/bookings/${b.id}`} className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-brand">{b.booking_code}</div>
                  <div className="font-medium mt-1 truncate">{clientName(b.client)}</div>
                  <div className="text-xs text-[#5C6066] truncate">{b.event_name}</div>
                </Link>
                <div className="flex items-start gap-1.5 flex-shrink-0">
                  <StatusBadge status={b.status} label={BOOKING_STATUS_LABEL[b.status]} />
                  {mgmt && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 -mr-1 -mt-1" data-testid={`booking-mobile-actions-${b.booking_code}`}>
                          <MoreHorizontal className="w-4 h-4"/>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem asChild><Link to={`/bookings/${b.id}`}><Eye className="w-3.5 h-3.5 mr-2"/> Lihat Detail</Link></DropdownMenuItem>
                        {!b.archived && <DropdownMenuItem asChild><Link to={`/bookings/${b.id}`}><Pencil className="w-3.5 h-3.5 mr-2"/> Edit Pemesanan</Link></DropdownMenuItem>}
                        {!b.archived && <DropdownMenuItem onClick={() => setArchiveTarget(b)}><Archive className="w-3.5 h-3.5 mr-2"/> Arsipkan Pemesanan</DropdownMenuItem>}
                        {b.archived && <DropdownMenuItem onClick={() => doRestore(b)}><ArchiveRestore className="w-3.5 h-3.5 mr-2"/> Pulihkan</DropdownMenuItem>}
                        {owner && <>
                          <DropdownMenuSeparator/>
                          <DropdownMenuItem onClick={() => openDelete(b)} className="text-rose-700 focus:text-rose-800 focus:bg-rose-50">
                            <Trash2 className="w-3.5 h-3.5 mr-2"/> Hapus Permanen
                          </DropdownMenuItem>
                        </>}
                        {canForce && <>
                          <DropdownMenuSeparator/>
                          <DropdownMenuItem onClick={() => setForceDeleteTarget(b)} className="text-rose-900 focus:text-rose-900 focus:bg-rose-100 font-semibold">
                            <Flame className="w-3.5 h-3.5 mr-2"/> Force Delete Permanen
                          </DropdownMenuItem>
                        </>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              <Link to={`/bookings/${b.id}`} className="block">
                <div className="mt-2 text-xs text-[#5C6066]">{formatDate(b.event_date)} • {b.venue || "-"}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(b.divisions || []).map((d) => (
                    <Badge key={d.id} variant="outline" className="text-[10px]">{d.short}</Badge>
                  ))}
                </div>
              </Link>
              {financial && (
                <Link to={`/bookings/${b.id}`} className="mt-2 flex justify-between text-xs">
                  <span className="text-[#8C9096]">Sisa: <span className="font-mono">{formatIDRShort(b.remaining)}</span></span>
                  <StatusBadge status={b.payment_status} />
                </Link>
              )}
            </div>
          ))}
        </div>
      </Card>

      <ConfirmDeleteModal
        open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Hapus Pemesanan?"
        message={deleteTarget ? `Data ${deleteTarget.booking_code} akan dihapus permanen dan tidak dapat dikembalikan.` : ""}
        itemLabel={deleteTarget ? `${deleteTarget.booking_code} — ${clientName(deleteTarget.client)}` : ""}
        onConfirm={doDelete}
      />

      <ForceDeleteModal
        open={!!forceDeleteTarget}
        onOpenChange={(v) => !v && setForceDeleteTarget(null)}
        entityType="booking"
        entityId={forceDeleteTarget?.id}
        entityLabel={forceDeleteTarget ? `${forceDeleteTarget.booking_code} — ${clientName(forceDeleteTarget.client)}` : ""}
        onDone={() => { setForceDeleteTarget(null); load(); }}
      />

      <Dialog open={!!archiveTarget} onOpenChange={(v) => !v && setArchiveTarget(null)}>
        <DialogContent data-testid="booking-archive-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Archive className="w-4 h-4"/> Arsipkan Pemesanan</DialogTitle>
            <DialogDescription>Pemesanan tidak akan muncul di daftar aktif. Semua histori (invoice, pembayaran, tugas) tetap tersimpan dan dapat dipulihkan.</DialogDescription>
          </DialogHeader>
          {archiveTarget && <div className="p-2 bg-[#FAF6EE] border border-[#E5E0D8] rounded text-sm font-medium">{archiveTarget.booking_code} — {clientName(archiveTarget.client)}</div>}
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Alasan (opsional)</Label>
            <Textarea rows={2} value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} className="mt-1.5" data-testid="booking-archive-reason"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveTarget(null)}>Batal</Button>
            <Button onClick={doArchive} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="booking-archive-confirm">
              {busy ? "Mengarsipkan..." : "Arsipkan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConflict} onOpenChange={(v) => !v && setDeleteConflict(null)}>
        <DialogContent data-testid="booking-delete-conflict-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="w-5 h-5"/> Tidak dapat dihapus permanen</DialogTitle>
            <DialogDescription>Pemesanan ini sudah memiliki histori dan tidak dapat dihapus permanen. Silakan arsipkan.</DialogDescription>
          </DialogHeader>
          {deleteConflict && (
            <div className="space-y-3">
              <div className="p-2 bg-[#FAF6EE] border border-[#E5E0D8] rounded text-sm font-medium">{deleteConflict.booking.booking_code} — {clientName(deleteConflict.booking.client)}</div>
              <div className="grid grid-cols-3 gap-2 text-center">
                {Object.entries(deleteConflict.relations).filter(([k, v]) => k !== "total" && v > 0).map(([k, v]) => (
                  <div key={k} className="p-2 bg-amber-50 border border-amber-200 rounded">
                    <div className="font-mono font-semibold">{v}</div>
                    <div className="text-[10px] uppercase text-amber-800">{k}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConflict(null)}>Tutup</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="booking-conflict-archive"
              onClick={() => { setArchiveTarget(deleteConflict.booking); setDeleteConflict(null); }}>
              <Archive className="w-3.5 h-3.5 mr-1"/> Arsipkan Saja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
